import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import api, { API_BASE } from '../../utils/api';
import { Modal, Button, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';

interface Unit {
    _id?: string;
    project: string;
    tower: string;
    floor: string;
    unitNumber: string;
    unitType: string;
    area: string;
    price: number;
    status: 'Available' | 'Hold' | 'Booked' | 'Sold' | 'Blocked';
    customerName?: string;
    customerMobile?: string;
    notes?: string;
}

interface Project {
    _id: string;
    name: string;
}

export default function AdminInventory() {
    const { user, token } = useAuth();
    const [projects, setProjects] = useState<Project[]>([]);
    const [selectedProject, setSelectedProject] = useState('');
    const [units, setUnits] = useState<Unit[]>([]);
    const [loading, setLoading] = useState(false);

    // Modal states
    const [showModal, setShowModal] = useState(false);
    const [editingUnit, setEditingUnit] = useState<Partial<Unit>>({});

    useEffect(() => {
        if (!token) return;
        if (user?.role === 'developer') {
            api.getMyProjects(token).then(setProjects).catch(console.error);
        } else {
            api.getAllProjects().then(setProjects).catch(console.error);
        }
    }, [token, user]);

    useEffect(() => {
        if (selectedProject) {
            fetchUnits();
        }
    }, [selectedProject]);

    const fetchUnits = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/inventory/${selectedProject}`);
            const data = await res.json();
            setUnits(data);
        } catch (err) {
            toast.error('Failed to load inventory');
        } finally {
            setLoading(false);
        }
    };

    const handleEditUnit = (unit: Unit) => {
        setEditingUnit(unit);
        setShowModal(true);
    };

    const handleSaveUnit = async () => {
        try {
            const token = localStorage.getItem('resido_token');
            const res = await fetch(`${API_BASE}/inventory/update`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(editingUnit)
            });

            if (res.ok) {
                toast.success('Unit state synchronized successfully');
                setShowModal(false);
                fetchUnits();
            } else {
                toast.error('Synchronization failed');
            }
        } catch (err) {
            toast.error('Linkage error detected');
        }
    };

    const statusConfig: Record<string, { bg: string, text: string, ring: string }> = {
        Available: { bg: 'bg-emerald-500', text: 'text-emerald-500', ring: 'ring-emerald-500/20' },
        Hold: { bg: 'bg-amber-500', text: 'text-amber-500', ring: 'ring-amber-500/20' },
        Booked: { bg: 'bg-blue-500', text: 'text-blue-500', ring: 'ring-blue-500/20' },
        Sold: { bg: 'bg-rose-500', text: 'text-rose-500', ring: 'ring-rose-500/20' },
        Blocked: { bg: 'bg-slate-200', text: 'text-blue-950', ring: 'ring-slate-500/20' }
    };

    const towers = Array.from(new Set(units.map(u => u.tower))).sort();

    return (
        <AdminLayout>
            <div className="animate__animated animate__fadeIn">
                {/* Header Section */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
                    <div>
                        <h1 className="text-3xl md:text-4xl font-bold text-black mb-2 leading-none">Inventory Matrix</h1>
                        <p className="text-[14px] md:text-[15px] font-bold text-black mt-3">Visual tracking and unit management in real-time across your portfolio.</p>
                    </div>
                    
                    <div className="relative group min-w-[340px]">
                        <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none">
                            <i className="fa-solid fa-building-circle-check text-black/30 text-base"></i>
                        </div>
                        <select
                            className="block w-full pl-14 pr-10 py-4 bg-white border border-gray-100 rounded-md text-[13px] font-bold focus:ring-2 focus:ring-[#05144b] transition-all text-black appearance-none cursor-pointer shadow-sm"
                            value={selectedProject}
                            onChange={(e) => setSelectedProject(e.target.value)}
                        >
                            <option value="">Select project protocol</option>
                            {projects.map(p => (
                                <option key={p._id} value={p._id}>{p.name}</option>
                            ))}
                        </select>
                        <div className="absolute inset-y-0 right-4 flex items-center pointer-events-none">
                            <i className="fa-solid fa-chevron-down text-[10px] text-blue-950"></i>
                        </div>
                    </div>
                </div>

                <div className="bg-white dark:bg-slate-900 rounded-md border border-blue-900/10 dark:border-slate-800 shadow-sm overflow-hidden min-h-[500px]">
                    {/* Status Dashboard / Legend */}
                    <div className="p-8 border-b border-blue-900/10 dark:border-slate-800 bg-white/50 dark:bg-slate-800/30">
                        <div className="flex flex-wrap items-center justify-center gap-8">
                            {Object.entries(statusConfig).map(([status, config]) => (
                                <div key={status} className="flex items-center gap-3">
                                    <div className={`w-3.5 h-3.5 rounded-sm ${config.bg} shadow-sm`}></div>
                                    <span className="text-[12px] font-bold text-black">{status}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="p-8 md:p-12">
                        {!selectedProject ? (
                            <div className="flex flex-col items-center justify-center py-24 text-center">
                                <div className="w-20 h-20 bg-white dark:bg-slate-800 rounded-md flex items-center justify-center mb-8 border-2 border-blue-900/10 dark:border-slate-700 shadow-inner">
                                    <i className="fa-solid fa-layer-group text-3xl text-blue-900"></i>
                                </div>
                                <h5 className="text-xl font-bold text-black mb-2">Awaiting Selection</h5>
                                <p className="text-black/60 text-[14px] font-bold">Please select a project protocol to initialize the inventory matrix.</p>
                            </div>
                        ) : loading ? (
                            <div className="flex flex-col items-center justify-center py-24">
                                <div className="w-10 h-10 border-4 border-[#05144b] border-t-transparent rounded-full animate-spin mb-6"></div>
                                <p className="text-blue-950 font-bold text-[13px] italic">Synchronizing unit data...</p>
                            </div>
                        ) : (
                            <div className="space-y-20">
                                {towers.map(tower => {
                                    const towerUnits = units.filter(u => u.tower === tower);
                                    const floors = Array.from(new Set(towerUnits.map(u => u.floor))).sort((a, b) => parseInt(b) - parseInt(a));

                                    return (
                                        <div key={tower} className="animate-in fade-in slide-in-from-bottom-4 duration-700">
                                            <div className="flex items-center gap-4 mb-10">
                                                <div className="w-11 h-11 bg-white border border-[#05144b] text-[#05144b] rounded-md flex items-center justify-center shadow-lg">
                                                    <i className="fa-solid fa-city text-base"></i>
                                                </div>
                                                <h4 className="text-xl font-bold text-black capitalize">Section Tower {tower}</h4>
                                                <div className="h-px bg-blue-50 dark:bg-slate-800 flex-grow"></div>
                                            </div>

                                            <div className="space-y-4">
                                                {floors.map(floor => (
                                                    <div key={floor} className="flex items-center gap-8 group">
                                                        <div className="w-24 hidden md:block">
                                                            <span className="text-[14px] font-bold text-black">Floor {floor}</span>
                                                        </div>
                                                        <div className="flex-grow flex flex-wrap gap-3">
                                                            {towerUnits.filter(u => u.floor === floor).map(unit => (
                                                                <button
                                                                    key={unit._id}
                                                                    className={`relative flex flex-col items-center justify-center w-16 h-16 md:w-18 md:h-18 rounded-md border-2 transition-all duration-300 transform hover:scale-105 active:scale-95 group/unit shadow-sm ${
                                                                        statusConfig[unit.status]?.bg || 'bg-slate-200'
                                                                    } border-white/30 hover:shadow-xl hover:z-10`}
                                                                    onClick={() => handleEditUnit(unit)}
                                                                >
                                                                    <span className="text-[13px] font-black text-white tracking-widest">{unit.unitNumber}</span>
                                                                    <div className="mt-1.5 flex gap-1">
                                                                        {[1, 2, 3].map(i => (
                                                                            <div key={i} className="w-1 h-1 bg-white/50 rounded-full"></div>
                                                                        ))}
                                                                    </div>
                                                                    
                                                                    {/* Hover Tooltip */}
                                                                    <div className="absolute -top-14 left-1/2 -translate-x-1/2 px-4 py-2 bg-[#05144b] text-white text-[11px] font-bold rounded-md opacity-0 group-hover/unit:opacity-100 pointer-events-none transition-opacity shadow-2xl whitespace-nowrap z-20 italic">
                                                                        {unit.unitType} • {unit.status}
                                                                        <div className="absolute top-full left-1/2 -translate-x-1/2 border-[6px] border-transparent border-t-[#05144b]"></div>
                                                                    </div>
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
                {/* Custom Redesigned Modal */}
                {showModal && editingUnit && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                        <div className="absolute inset-0 bg-[#05144b]/20 backdrop-blur-sm animate-in fade-in duration-300" onClick={() => setShowModal(false)}></div>
                        <div className="relative w-full max-w-2xl bg-white dark:bg-slate-900 rounded-md shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300 border border-blue-900/10 dark:border-slate-800">
                            {/* Modal Header */}
                            <div className="px-5 py-3 border-b border-blue-900/10 dark:border-slate-800 flex items-center justify-between bg-white">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-white border border-[#05144b] text-[#05144b] rounded-md flex items-center justify-center shadow-lg">
                                        <i className="fa-solid fa-sliders text-base"></i>
                                    </div>
                                    <div>
                                        <h5 className="text-xl font-bold text-black leading-none">Update Unit {editingUnit.tower}-{editingUnit.unitNumber}</h5>
                                        <p className="text-[12px] font-bold text-black/60 mt-1">Configuration Protocol</p>
                                    </div>
                                </div>
                                <button onClick={() => setShowModal(false)} className="w-8 h-8 rounded-full hover:bg-blue-50 dark:hover:bg-slate-800 text-blue-950 transition-colors flex items-center justify-center">
                                    <i className="fa-solid fa-xmark text-base"></i>
                                </button>
                            </div>

                            {/* Modal Body */}
                            <div className="p-4 max-h-[70vh] overflow-y-auto scrollbar-hide">
                                {/* Unit Configuration Grid */}
                                <div className="grid grid-cols-2 lg:grid-cols-4 gap-1.5 mb-3">
                                    {[
                                        { label: 'Topology', value: editingUnit.unitType, icon: 'fa-cubes' },
                                        { label: 'Surface area', value: editingUnit.area, icon: 'fa-ruler-combined' },
                                        { label: 'Market valuation', value: `₹${editingUnit.price?.toLocaleString('en-IN')}`, icon: 'fa-tag' },
                                        { label: 'Matrix position', value: `Floor ${editingUnit.floor}`, icon: 'fa-stairs' }
                                    ].map((stat, i) => (
                                        <div key={i} className="p-2 bg-white border-2 border-blue-900/10 rounded-md shadow-sm">
                                            <div className="flex items-center gap-2 mb-1">
                                                <i className={`fa-solid ${stat.icon} text-xs text-blue-950`}></i>
                                                <span className="text-[12px] font-black text-blue-950 italic capitalize">{stat.label}</span>
                                            </div>
                                            <div className="text-[14px] font-black text-blue-950 dark:text-white italic leading-tight break-words">{stat.value || 'N/A'}</div>
                                        </div>
                                    ))}
                                </div>

                                <div className="space-y-5">
                                    {/* State Selection */}
                                    <div>
                                        <h6 className="text-[14px] font-black text-blue-950 dark:text-white mb-3 flex items-center gap-2 italic">
                                            <div className="w-1.5 h-4 bg-[#05144b] rounded-full"></div>
                                            State selection protocol
                                        </h6>
                                        <div className="flex flex-wrap gap-2">
                                            {['Available', 'Hold', 'Booked', 'Sold', 'Blocked'].map(s => (
                                                <button
                                                    key={s}
                                                    onClick={() => setEditingUnit({ ...editingUnit, status: s as any })}
                                                    className={`px-5 py-2 rounded-md text-[13px] font-bold transition-all border-2 ${
                                                        editingUnit.status === s 
                                                        ? 'bg-white text-[#05144b] border-[#05144b] shadow-lg active:scale-95' 
                                                        : 'bg-white text-black border-gray-100 hover:border-[#05144b]/30'
                                                    }`}
                                                >
                                                    {s}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Principal Documentation */}
                                    <div>
                                        <h6 className="text-[15px] font-bold text-black mb-3 flex items-center gap-2">
                                            <div className="w-1.5 h-4 bg-[#05144b] rounded-full"></div>
                                            Principal Documentation
                                        </h6>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="space-y-1.5">
                                                <label className="text-[13px] font-black text-blue-950 ml-1 italic capitalize">Customer Identifier</label>
                                                <input
                                                    type="text"
                                                    className="block w-full px-4 py-2.5 bg-white border-2 border-blue-900/10 focus:border-blue-950 rounded-md text-[14px] font-bold text-blue-950 transition-all outline-none shadow-sm placeholder:text-blue-900"
                                                    placeholder="Full legal name"
                                                    value={editingUnit.customerName || ''}
                                                    onChange={(e) => setEditingUnit({ ...editingUnit, customerName: e.target.value })}
                                                />
                                            </div>
                                            <div className="space-y-1.5">
                                                <label className="text-[13px] font-black text-blue-950 ml-1 italic capitalize">Communication link</label>
                                                <input
                                                    type="text"
                                                    className="block w-full px-4 py-2.5 bg-white border-2 border-blue-900/10 focus:border-blue-950 rounded-md text-[14px] font-bold text-blue-950 transition-all outline-none shadow-sm placeholder:text-blue-900"
                                                    placeholder="+91 00000 00000"
                                                    value={editingUnit.customerMobile || ''}
                                                    onChange={(e) => setEditingUnit({ ...editingUnit, customerMobile: e.target.value })}
                                                />
                                            </div>
                                            <div className="md:col-span-2 space-y-1.5">
                                                <label className="text-[13px] font-black text-blue-950 ml-1 italic capitalize">Executive annotations</label>
                                                <textarea
                                                    rows={2}
                                                    className="block w-full px-4 py-2.5 bg-white border-2 border-blue-900/10 focus:border-blue-950 rounded-md text-[14px] font-bold text-blue-950 transition-all outline-none resize-none shadow-sm placeholder:text-blue-900"
                                                    placeholder="Annotations..."
                                                    value={editingUnit.notes || ''}
                                                    onChange={(e) => setEditingUnit({ ...editingUnit, notes: e.target.value })}
                                                ></textarea>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Modal Footer */}
                            <div className="px-5 py-4 border-t border-blue-900/10 dark:border-slate-800 flex items-center justify-end gap-3 bg-white">
                                <button
                                    onClick={() => setShowModal(false)}
                                    className="px-6 py-2 text-[14px] font-black text-blue-950/50 hover:text-blue-950 transition-colors italic"
                                >
                                    Discard
                                </button>
                                <button
                                    onClick={handleSaveUnit}
                                    className="px-10 py-3 bg-white border-2 border-[#05144b] text-[#05144b] hover:bg-blue-50 text-[14px] font-bold rounded-md shadow-lg active:scale-95 transition-all"
                                >
                                    Synchronize State
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </AdminLayout>
    );
}
